import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageRoute } from "../../../models";
import { getChat, markMessageAsRead, selectChat, blockUser, unblockUser, selectChatParticipants, getUnreadCount } from "../../../routes/Chat/chat.ducks";
import styles from "../chatPage.scss";

import { Card } from "antd";
import { useHistory } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import { getIconUrl } from "../../../services/asset";
import { getWindowWidth, isMobileScreen, notification, scrollToTop } from "../../../services";
import { UserI } from "../../../typings/interfaces";
import { TextForm } from "./TextForm";
import { useOtherChatParticipantInfo } from "../hooks/useOtherChatParticipantInfo";
import { MessagesList } from "./MessagesList";
import { MESSAGES_LIST_DIV_ID } from "../constants/constants";
import { isScrolledToBottom } from "../utils/isScrolledToBottom";
import { getLastMessageIdFromChatParticipant } from "../utils/getLastMessageIdFromChatParticipant";
import { AdInfo } from "./AdInfo/AdInfo";
import HelmetComponent from "../../../utils/HelmetHelper";
import { HeaderCard } from "./HeaderCard";
import Skeleton from "../../../components/Skeleton";


type Props = {
  adId?: string;
  chatId?: string;
  user: UserI;
}

export const SingleChat = ({ adId, chatId, user }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const chat = useSelector(selectChat);
  const chatParticipants = useSelector(selectChatParticipants);
  const [isLoading, setIsLoading] = useState(true);
  const [initialFirstUnreadIndex, setInitialFirstUnreadIndex] = useState(-1);
  const otherChatParticipantInfo = useOtherChatParticipantInfo(chat, user)
  const isMobile = isMobileScreen(getWindowWidth());

  const handleMarkMessageAsRead = () => {
    const lastMessageIdFromOtherChatParticipant = getLastMessageIdFromChatParticipant(otherChatParticipantInfo.userId, chat.messages)
    const lastReadMessageKey = user.userId === chat.senderId ? 'senderLastReadMessageId' : 'recipientLastReadMessageId';
    const lastReadMessageId = chat[lastReadMessageKey];

    if (chatId && lastMessageIdFromOtherChatParticipant && lastMessageIdFromOtherChatParticipant !== lastReadMessageId) {
      dispatch(markMessageAsRead(chatId, lastMessageIdFromOtherChatParticipant, (response, error) => {
        dispatch(getUnreadCount());
      }))
    }

  }

  const loadChat = () => {
    if (document.visibilityState === "hidden") return;

    dispatch(getChat({ chatId, adId }, (response, error) => {
      if (error) {
        console.log("Error loading chat:", error);
        notification.showError("Razgovor nije pronađen");
        history.push(PageRoute.chats);
      }

      // Compute first unread index from raw API response (only on initial load)
      if (isLoading && response && response.messages) {
        const key = user.userId === response.senderId ? "senderLastReadMessageId" : "recipientLastReadMessageId";
        const lastReadMessageId = response[key];
        const otherUserId = user.userId === response.senderId ? response.recipientId : response.senderId;

        if (!lastReadMessageId) {
          // Never read - find first message from the other person
          setInitialFirstUnreadIndex(response.messages.findIndex(m => m.senderId === otherUserId));
        } else {
          const lastReadIndex = response.messages.findIndex(m => m.messageId === lastReadMessageId);
          if (lastReadIndex === -1 || lastReadIndex >= response.messages.length - 1) {
            setInitialFirstUnreadIndex(-1);
          } else {
            // Find first message FROM THE OTHER PERSON after lastReadIndex
            const firstUnread = response.messages.findIndex((m, i) => i > lastReadIndex && m.senderId === otherUserId);
            setInitialFirstUnreadIndex(firstUnread);
          }
        }
      }

      setIsLoading(false);
    }));
  }


  useEffect(() => {
    scrollToTop();
    loadChat()
    const interval = setInterval(loadChat, 20000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!isLoading && chat) {
      handleMarkMessageAsRead()
    }
  }, [isLoading, chat])


  const handleTextFormClick = () => {
    if (!chat || !chat.messages || chat.messages.length === 0) return;

    const messagesListDiv = document.getElementById(MESSAGES_LIST_DIV_ID)

    if (isScrolledToBottom(messagesListDiv)) {
      handleMarkMessageAsRead()
    }
  }

  // Check if current user blocked the other user
  const isOtherUserBlocked = chatParticipants ?
    ((user && user.userId) === (chat && chat.senderId) ? chatParticipants.isRecipientBlocked : chatParticipants.isSenderBlocked) :
    false;

  // Check if current user is blocked by the other user
  const isCurrentUserBlocked = chatParticipants ?
    ((user && user.userId) === (chat && chat.senderId) ? chatParticipants.isSenderBlocked : chatParticipants.isRecipientBlocked) :
    false;

  const isOtherUserBlockedByAdmin = chat && chat.isUserBlockedByAdmin

  // Any participant is blocked
  const isAnyParticipantBlocked = isOtherUserBlocked || isCurrentUserBlocked || isOtherUserBlockedByAdmin;

  return (
    <React.Fragment>
      <HelmetComponent
        title={chat ? `${otherChatParticipantInfo.name} | INDEX OGLASI` : "Poruke | INDEX OGLASI"}
        index={false}
      />
      <div className={styles.singleChatWrapper}>
        <HeaderCard
          chatId={chatId}
          user={user}
          otherChatParticipantInfo={otherChatParticipantInfo}
          chat={chat}
          isOtherUserBlocked={isOtherUserBlocked}
        />
        {chat && (
          <AdInfo
            chat={chat}
            isMobile={isMobile}
          />
        )}
        <div className={styles.singleChatCard}>
          {isLoading && (
            <React.Fragment>
              <Skeleton className={`${styles.skeletonMessage} ${styles.isOwn}`} />
              <Skeleton className={styles.skeletonMessage} />
              <Skeleton className={`${styles.skeletonMessage} ${styles.isOwn}`} />
              <Skeleton className={styles.skeletonMessage} />
              <Skeleton className={`${styles.skeletonMessage} ${styles.isOwn}`} />
              <Skeleton className={styles.skeletonMessage} />
              <Skeleton className={`${styles.skeletonMessage} ${styles.isOwn}`} />
              <Skeleton className={styles.skeletonMessage} />
            </React.Fragment>
          )}
          {!isLoading && chat && (
            <React.Fragment>
              <MessagesList
                key={chatId}
                messages={chat.messages}
                user={user}
                otherChatParticipantInfo={otherChatParticipantInfo}
                isMobile={isMobile}
                initialFirstUnreadIndex={initialFirstUnreadIndex}
                senderContactInfo={chat.senderContactInfo}
              />
              {isAnyParticipantBlocked ? (
                <div className={styles.blockedIndicator}>
                  <img src={getIconUrl("blocked.svg")} alt="" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                  <span>
                    {isOtherUserBlockedByAdmin && "Korisnik je blokiran. Nastavak razgovora nije moguć."}
                    {!isOtherUserBlockedByAdmin && isOtherUserBlocked && "Blokirali ste ovog korisnika. Ne možete slati nove poruke."}
                    {!isOtherUserBlockedByAdmin && isCurrentUserBlocked && "Korisnik vas je blokirao. Ne možete slati nove poruke."}
                  </span>
                </div>
              ) : (
                <TextForm
                  adId={adId}
                  chatId={chatId}
                  handleTextFormClick={handleTextFormClick}
                  isMobile={isMobile}
                  chat={chat}
                />
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/SingleChat.tsx